import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Heart, MessageCircle, Flag, Trash2, Image as ImageIcon, X, HelpCircle, CheckCircle2, Send , Ban } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ShareButton from '../components/ShareButton';
import Lightbox from '../components/Lightbox';
import CopyrightNotice from '../components/CopyrightNotice';
import api, { mediaUrl, uploadImage, errText } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const CATEGORIES = [['all', 'All'], ['general', 'General'], ['meditation', 'Meditation'], ['qa', 'Q&A'], ['events', 'Events'], ['videos', 'Videos']];
const SOURCE_LABEL = { osho: 'Official', teacher: 'Teacher', facilitator: 'Facilitator', other: 'Shared' };
const CAT_LABEL = { general: 'General', meditation: 'Meditation', qa: 'Q&A', events: 'Events' };

// Server already strips disallowed member links (they arrive as "[link removed]" plain text),
// so any URL that survives here is safe to render as a clickable link.
const URL_G = /(https?:\/\/[^\s<>"')]+)/gi;
const stripTrail = (u) => u.replace(/[.,;:)!?'"]+$/, '');
const renderContent = (text) => {
  const parts = (text || '').split(URL_G);
  return parts.map((p, i) => /^https?:\/\//i.test(p)
    ? <a key={i} href={p} target="_blank" rel="nofollow ugc noopener noreferrer" className="text-rose-italic underline break-all hover:text-mahogany">{p}</a>
    : <span key={i}>{p}</span>);
};

const youtubeId = (u) => {
  const m = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/|v\/)|youtu\.be\/)([\w-]{6,})/i);
  return m ? m[1] : null;
};
const extractUrls = (t) => Array.from(new Set(((t || '').match(URL_G) || []).map(stripTrail)));

const MediaEmbed = ({ url }) => {
  const yt = youtubeId(url);
  if (yt) return (
    <div className="mt-3 rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="youtube-embed">
      <iframe title="YouTube video" src={`https://www.youtube.com/embed/${yt}`} className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
    </div>
  );
  const fb = facebookVideo(url);
  if (fb) return (
    <div className="mt-3 rounded-xl overflow-hidden bg-black mx-auto" style={{ aspectRatio: fb.reel ? '9 / 16' : '16 / 9', maxWidth: fb.reel ? 360 : '100%' }} data-testid="facebook-embed">
      <iframe title="Facebook video" src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=${fb.reel ? 360 : 560}`}
        className="w-full h-full border-0" scrolling="no"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowFullScreen />
    </div>
  );
  const ig = instagramCode(url);
  if (ig) return (
    <div className="mt-3 rounded-xl overflow-hidden bg-white mx-auto border border-rose/30" style={{ maxWidth: 400, height: 640 }} data-testid="instagram-embed">
      <iframe title="Instagram post" src={`https://www.instagram.com/${ig.kind}/${ig.code}/embed`} className="w-full h-full border-0" scrolling="no" allowFullScreen />
    </div>
  );
  const tt = tiktokId(url);
  if (tt) return (
    <div className="mt-3 rounded-xl overflow-hidden bg-black mx-auto" style={{ maxWidth: 340, height: 600 }} data-testid="tiktok-embed">
      <iframe title="TikTok video" src={`https://www.tiktok.com/embed/v2/${tt}`} className="w-full h-full border-0"
        allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
    </div>
  );
  return <LinkPreview url={url} />;
};

const instagramCode = (u) => {
  const m = u.match(/instagram\.com\/(?:[\w.]+\/)?(p|reels?|tv)\/([\w-]+)/i);
  return m ? { kind: m[1].toLowerCase() === 'reels' ? 'reel' : m[1].toLowerCase(), code: m[2] } : null;
};
const tiktokId = (u) => {
  const m = u.match(/tiktok\.com\/(?:@[^/]+\/video|embed(?:\/v2)?)\/(\d+)/i);
  return m ? m[1] : null;
};

const facebookVideo = (u) => {
  if (/fb\.watch\//i.test(u)) return { reel: false };
  if (!/(?:facebook|fb)\.com\//i.test(u)) return null;
  if (/\/reel\/|\/share\/r\//i.test(u)) return { reel: true };
  if (/\/videos\/|\/watch\/?\?v=|\/watch\/live|video\.php|\/share\/v\//i.test(u)) return { reel: false };
  return null;
};

const LinkPreview = ({ url }) => {
  const [d, setD] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let ok = true;
    api.get('/community/link-preview', { params: { url } }).then((r) => ok && setD(r.data)).catch(() => ok && setD({ url }));
    return () => { ok = false; };
  }, [url]);
  let host = url;
  try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { /* noop */ }
  if (hidden) return null;
  return (
    <a href={url} target="_blank" rel="nofollow ugc noopener noreferrer" data-testid="link-preview"
      className="mt-3 block rounded-xl border border-rose/40 overflow-hidden hover:bg-cream-2/50 transition-colors">
      {d?.image && <img src={d.image} alt="" loading="lazy" className="w-full max-h-72 object-cover" onError={() => setHidden(false)} />}
      <div className="p-3">
        <div className="text-[11px] uppercase tracking-widest text-mahogany/60">{d?.site || host}</div>
        <div className="text-burgundy font-medium mt-0.5 line-clamp-2">{d?.title || url}</div>
        {d?.description && <div className="text-sm text-mahogany/70 mt-1 line-clamp-2">{d.description}</div>}
      </div>
    </a>
  );
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};


const CommunityFeed = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [category, setCategory] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/community/posts', { params: category !== 'all' ? { category } : {} }); setPosts(r.data || []); }
    catch { setPosts([]); } finally { setLoading(false); }
  }, [category]);
  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const isAdmin = user?.roles?.includes('admin');

  return (
    <div className="App" data-testid="page-community">
      <Header />
      <main className="bg-cream min-h-[70vh]">
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
          <div className="text-center">
            <div className="eyebrow mb-3">Community</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-burgundy leading-tight">Sangha <span className="font-italic-display text-rose-italic">Feed</span></h1>
            <p className="mt-5 text-mahogany/80 max-w-2xl mx-auto">Share reflections, ask questions and celebrate the journey together. Be kind and authentic — this is a space of awareness.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-8">
            <div className="space-y-5 min-w-0">
              {user ? <Composer isAdmin={isAdmin} onPosted={fetchPosts} />
                : (
                  <div className="p-5 rounded-2xl bg-white/70 border border-rose/50 flex items-center justify-between flex-wrap gap-3" data-testid="community-signin-prompt">
                    <span className="text-burgundy/85">Sign in to share, like and comment.</span>
                    <Link to="/login" className="pill-btn">Sign in</Link>
                  </div>
                )}

              {/* Mobile category chips */}
              <div className="flex lg:hidden gap-2 overflow-x-auto pb-1">
                {CATEGORIES.map(([v, l]) => (
                  <button key={v} onClick={() => setCategory(v)} className={`shrink-0 px-3 py-1.5 rounded-full text-sm ${category === v ? 'bg-mahogany text-cream' : 'bg-white/70 text-mahogany/70 border border-rose/40'}`}>{l}</button>
                ))}
              </div>

              {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
                : posts.length === 0 ? <div className="p-10 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="community-empty">No posts yet. Be the first to share 🌸</div>
                : (
                  <div className="space-y-5" data-testid="community-posts">
                    {posts.map((p) => <PostCard key={p.id} post={p} user={user} isAdmin={isAdmin} onChanged={fetchPosts} navigate={navigate} toast={toast} />)}
                  </div>
                )}
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block space-y-5">
              <div className="p-5 rounded-2xl bg-white/70 border border-rose/50 sticky top-24">
                <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-3">Topics</div>
                <div className="flex flex-col gap-1.5" data-testid="community-category-filter">
                  {CATEGORIES.map(([v, l]) => (
                    <button key={v} onClick={() => setCategory(v)} data-testid={`community-cat-${v}`}
                      className={`text-left px-3 py-2 rounded-xl text-sm transition-colors ${category === v ? 'bg-rose-soft text-mahogany font-medium' : 'text-burgundy/80 hover:bg-rose-soft/50'}`}>{l}</button>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-rose/30 text-xs text-mahogany/60 leading-relaxed">
                  <span className="font-medium text-burgundy/80">Community guidelines</span><br />
                  Be respectful. No spam or promotions. Only YouTube, Facebook, Instagram &amp; TikTok links are allowed.
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-10">
            <CopyrightNotice compact />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const Composer = ({ isAdmin, onPosted }) => {
  const { toast } = useToast();
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [isQuestion, setIsQuestion] = useState(false);
  const [images, setImages] = useState([]);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const MAX_IMAGES = 10;

  const submit = async () => {
    if (!content.trim()) return toast({ title: 'Write something first' });
    setPosting(true);
    try {
      await api.post('/community/posts', { content, category, is_question: isQuestion, image_urls: images });
      setContent(''); setImages([]); setIsQuestion(false); setCategory('general');
      toast({ title: 'Posted 🌸' });
      onPosted();
    } catch (e) { toast({ title: 'Could not post', description: e?.response?.data?.detail }); }
    finally { setPosting(false); }
  };

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) { toast({ title: `You can add up to ${MAX_IMAGES} photos` }); return; }
    setUploading(true);
    try {
      const picked = files.slice(0, room);
      const uploaded = [];
      for (const f of picked) {
        try { uploaded.push(await uploadImage(f)); }
        catch (e) { toast({ title: 'Upload failed', description: errText(e, 'Please try a different image.') }); }
      }
      if (uploaded.length) setImages((prev) => [...prev, ...uploaded]);
      if (files.length > room) toast({ title: `Only ${room} more photo(s) added (max ${MAX_IMAGES})` });
    } finally { setUploading(false); }
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="p-5 rounded-2xl bg-white/80 border border-rose/50" data-testid="community-composer">
      <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} maxLength={5000}
        placeholder="Share a reflection or ask a question…" data-testid="composer-input"
        className="w-full bg-transparent focus:outline-none text-burgundy resize-none" />
      {images.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="composer-image-previews">
          {images.map((img, idx) => (
            <div key={img + idx} className="relative">
              <img src={mediaUrl(img)} alt="" className="h-28 w-28 object-cover rounded-xl border border-rose/40" />
              <button onClick={() => removeImage(idx)} data-testid={`composer-remove-image-${idx}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="composer-category"
            className="bg-white/70 rounded-full border border-rose/50 px-3 py-1.5 text-sm text-burgundy focus:outline-none">
            {CATEGORIES.filter(([v]) => v !== 'all' && v !== 'videos').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="pill-btn-outline py-1.5 text-sm cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Photos
            <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="composer-file-input"
              onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
          </label>
          <label className="flex items-center gap-1.5 text-sm text-burgundy cursor-pointer">
            <input type="checkbox" checked={isQuestion} onChange={(e) => setIsQuestion(e.target.checked)} data-testid="composer-question" /> Question
          </label>
        </div>
        <button onClick={submit} disabled={posting} className="pill-btn py-2" data-testid="composer-submit">
          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
        </button>
      </div>
      {!isAdmin && <p className="mt-2 text-[11px] text-mahogany/50">Only YouTube, Facebook, Instagram &amp; TikTok links are allowed. Other links are removed automatically.</p>}
    </div>
  );
};

const PostCard = ({ post, user, isAdmin, onChanged, navigate, toast }) => {
  const [liked, setLiked] = useState(post.liked);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [showComments, setShowComments] = useState(false);
  const [lb, setLb] = useState(-1);
  const canDelete = isAdmin || (user && user.user_id === post.author_id);
  const imgs = (post.image_urls && post.image_urls.length) ? post.image_urls : (post.image_url ? [post.image_url] : []);

  const like = async () => {
    if (!user) { navigate('/login'); return; }
    try { const r = await api.post(`/community/posts/${post.id}/like`); setLiked(r.data.liked); setLikeCount(r.data.like_count); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const block = async () => {
    if (!window.confirm(`Block ${post.author_name}? You won't see each other's posts, comments or chat.`)) return;
    try { const r = await api.post(`/community/block/${post.author_id}`); toast({ title: r.data.message }); setTimeout(() => window.location.reload(), 600); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const report = async () => {
    if (!user) { navigate('/login'); return; }
    try { const r = await api.post(`/community/posts/${post.id}/report`); toast({ title: r.data.message }); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this post?')) return;
    try { await api.delete(`/community/posts/${post.id}`); toast({ title: 'Deleted' }); onChanged(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const resolve = async () => {
    try { await api.post(`/community/posts/${post.id}/resolve`); onChanged(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const initials = (post.author_name || '?').split(' ').map((x) => x[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="p-5 rounded-2xl bg-white/80 border border-rose/50" data-testid={`post-${post.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-rose-soft flex items-center justify-center text-mahogany font-serif-display shrink-0 overflow-hidden">
            {post.author_avatar ? <img src={mediaUrl(post.author_avatar)} alt="" className="w-full h-full object-cover" /> : initials}
          </div>
          <div className="min-w-0">
            <div className="text-burgundy font-medium truncate">{post.author_name}</div>
            <div className="text-xs text-mahogany/60">
              {post.source_platform && <span className="mr-1.5 px-1.5 py-0.5 rounded bg-mahogany/10 text-mahogany text-[10px] uppercase tracking-wider" data-testid={`post-source-badge-${post.id}`}>{SOURCE_LABEL[post.source_owner_type] || 'Official'} · {post.source_platform}</span>}
              {timeAgo(post.created_at)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{CAT_LABEL[post.category] || 'General'}</span>
          {post.is_question && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${post.is_resolved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
              {post.is_resolved ? <CheckCircle2 className="w-3 h-3" /> : <HelpCircle className="w-3 h-3" />}{post.is_resolved ? 'Resolved' : 'Question'}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 text-burgundy/90 whitespace-pre-wrap break-words leading-relaxed">{renderContent(post.content)}</div>
      {imgs.length === 1 && (
        <button type="button" onClick={() => setLb(0)} data-testid={`post-image-${post.id}`} className="block mt-3">
          <img src={mediaUrl(imgs[0])} alt="" className="rounded-xl max-h-[420px] w-auto object-contain border border-rose/30 cursor-zoom-in" loading="lazy" />
        </button>
      )}
      {imgs.length > 1 && (
        <div className={`mt-3 grid gap-2 ${imgs.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`} data-testid={`post-gallery-${post.id}`}>
          {imgs.map((img, i) => (
            <button type="button" key={img + i} onClick={() => setLb(i)} data-testid={`post-image-${post.id}-${i}`} className="block relative">
              <img src={mediaUrl(img)} alt="" className="rounded-xl aspect-square w-full object-cover border border-rose/30 hover:opacity-90 transition-opacity cursor-zoom-in" loading="lazy" />
              {i === 3 && imgs.length > 4 && (
                <span className="absolute inset-0 rounded-xl bg-black/50 text-white flex items-center justify-center text-lg font-serif-display">+{imgs.length - 4}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {lb >= 0 && <Lightbox images={imgs.map(mediaUrl)} index={lb} onClose={() => setLb(-1)} />}
      {extractUrls(post.content).slice(0, 2).map((u) => <MediaEmbed key={u} url={u} />)}

      <div className="mt-4 flex items-center gap-4 text-sm text-mahogany/70 border-t border-rose/30 pt-3">
        <button onClick={like} data-testid={`like-${post.id}`} className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-rose-italic' : 'hover:text-mahogany'}`}>
          <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} /> {likeCount}
        </button>
        <button onClick={() => setShowComments((s) => !s)} data-testid={`comments-toggle-${post.id}`} className="flex items-center gap-1.5 hover:text-mahogany">
          <MessageCircle className="w-4 h-4" /> {post.comment_count}
        </button>
        {post.is_question && canDelete && (
          <button onClick={resolve} className="text-xs hover:text-green-700">{post.is_resolved ? 'Mark unresolved' : 'Mark resolved'}</button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <ShareButton compact url={`${window.location.origin}/community`} title="Osho Universal Community" text={(post.content || '').slice(0, 120)} testid={`share-post-${post.id}`} />
          <button onClick={report} data-testid={`report-${post.id}`} title="Report" className="hover:text-red-600"><Flag className="w-4 h-4" /></button>
          {user && user.user_id !== post.author_id && !post.author_is_team && !String(post.author_id || '').startsWith('source:') && <button onClick={block} data-testid={`block-${post.id}`} title="Block this member" className="hover:text-red-600"><Ban className="w-4 h-4" /></button>}
          {canDelete && <button onClick={remove} data-testid={`delete-post-${post.id}`} title="Delete" className="hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>

      {showComments && <Comments postId={post.id} user={user} isAdmin={isAdmin} navigate={navigate} toast={toast} onCountChange={onChanged} />}
    </div>
  );
};

const Comments = ({ postId, user, isAdmin, navigate, toast, onCountChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/community/posts/${postId}/comments`); setItems(r.data || []); }
    catch { setItems([]); } finally { setLoading(false); }
  }, [postId]);
  useEffect(() => { fetch(); }, [fetch]);

  const send = async () => {
    if (!user) { navigate('/login'); return; }
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/community/posts/${postId}/comments`, { content: text }); setText(''); await fetch(); onCountChange && onCountChange(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); } finally { setSending(false); }
  };
  const remove = async (c) => {
    try { await api.delete(`/community/comments/${c.id}`); await fetch(); onCountChange && onCountChange(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const report = async (c) => {
    try { const r = await api.post(`/community/comments/${c.id}/report`); toast({ title: r.data.message }); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-rose/30 space-y-3" data-testid={`comments-${postId}`}>
      {loading ? <div className="text-sm text-mahogany/60">Loading…</div>
        : items.map((c) => {
          const canDel = isAdmin || (user && user.user_id === c.author_id);
          const ci = (c.author_name || '?').split(' ').map((x) => x[0]).slice(0, 2).join('').toUpperCase();
          return (
            <div key={c.id} className="flex items-start gap-2.5" data-testid={`comment-${c.id}`}>
              <div className="w-7 h-7 rounded-full bg-rose-soft/70 flex items-center justify-center text-[11px] text-mahogany shrink-0 overflow-hidden">
                {c.author_avatar ? <img src={mediaUrl(c.author_avatar)} alt="" className="w-full h-full object-cover" /> : ci}
              </div>
              <div className="flex-1 bg-cream-2/60 rounded-xl px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-burgundy">{c.author_name} <span className="text-xs text-mahogany/50 font-normal">· {timeAgo(c.created_at)}</span></span>
                  <div className="flex items-center gap-2 text-mahogany/50">
                    {user && <button onClick={() => report(c)} title="Report" className="hover:text-red-600"><Flag className="w-3.5 h-3.5" /></button>}
                    {canDel && <button onClick={() => remove(c)} title="Delete" className="hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
                <div className="text-sm text-burgundy/85 whitespace-pre-wrap break-words">{renderContent(c.content)}</div>
              </div>
            </div>
          );
        })}
      {user ? (
        <div className="flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Write a comment…" data-testid={`comment-input-${postId}`}
            className="flex-1 bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-sm text-burgundy focus:outline-none" />
          <button onClick={send} disabled={sending} data-testid={`comment-submit-${postId}`} className="pill-btn py-2">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
        </div>
      ) : <div className="text-sm text-mahogany/60"><Link to="/login" className="text-rose-italic underline">Sign in</Link> to comment.</div>}
    </div>
  );
};

export default CommunityFeed;
